import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Button, List, Tag } from 'antd';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '@/features/auth';
import {
  api,
  NotificationRecordResponse,
  useGetNotificationsInfoForInstance,
  useMarkInstanceNotificationsAsSeen,
  useMarkNotificationAsSeen,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { EventTypes, NotificationType } from '@/shared/types';
import { Row } from '@/shared/ui';
import { formatTimeAgo, trigger } from '@/shared/utils';
import { captureError } from '@/shared/utils/error-reporting';

const NOTIFICATIONS_PER_PAGE = 50;

const notificationContent = (item: NotificationRecordResponse) => {
  switch (item.type) {
    case NotificationType.UserAddedComment:
      return (
        <div>
          {item.initiatorAccountType === 'workspaceMember' ? 'Author' : 'User'}{' '}
          added new comment on step{' '}
          <strong>"{item.instance.step.title}"</strong>
        </div>
      );
    case NotificationType.UserCompletedProc:
      return <div>User completed the proc.</div>;

    default:
      throw new Error(
        'Unknown notification type supplied to notificationContent [NotificationsSidebar.tsx]',
      );
  }
};

export const AuthorNotificationsSidebar = () => {
  const queryClient = useQueryClient();
  const { procId } = useParams();
  const { accessToken } = useAuth();
  const { theme } = useThemeContext();
  const { activeOrganization } = useRouterContext();

  const fetchNotifications = async ({ pageParam = 1 }) => {
    return await api.get(
      `/notifications/instances/author/${procId}?page=${pageParam}&take=${NOTIFICATIONS_PER_PAGE}&order=DESC`,
      { headers: { authorization: `Bearer ${accessToken}` } },
    );
  };

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    fetchNextPage,
    refetch: refetchNotifications,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['authorInstanceNotificationsGetPaginated', procId],
    queryFn: fetchNotifications,
    keepPreviousData: true,
    getNextPageParam: ({ data }) => {
      if (data.meta.hasNextPage) {
        return data.meta.page + 1;
      }
      return false;
    },
    enabled: procId !== undefined && Boolean(accessToken),
    select: (data) => data?.pages.flatMap((page) => page.data.data) as any,
  });

  const { data: unreadNotificationsCount } = useGetNotificationsInfoForInstance(
    {
      pathParams: { instanceId: procId!, viewType: 'author' },
    },
    { enabled: procId !== undefined, select: (data: any) => data?.totalUnread },
  );
  const { mutate: markAsSeen } = useMarkInstanceNotificationsAsSeen();
  const { mutate: markSingleNotificationsAsSeen } = useMarkNotificationAsSeen();

  const markInstanceNotificationsAsSeen = () => {
    try {
      markAsSeen(
        {
          pathParams: { instanceId: procId! },
        },
        {
          onSuccess: () => {
            // refetch global notifications
            queryClient.invalidateQueries([
              'globalNotificationsGetPaginated',
              activeOrganization?.id,
            ]);
            // refetch global notifications count
            queryClient.invalidateQueries([
              'notifications',
              activeOrganization?.id,
              'info',
            ]);
            // refetch notifications for this instance
            refetchNotifications();
            // refetch this instance as step's prop hasUnseenComments should be updated
            queryClient.invalidateQueries([
              'procs',
              'author',
              'instances',
              procId,
            ]);
            // refetch notifications count for this instance
            queryClient.invalidateQueries([
              'notifications',
              'instance',
              'author',
              procId,
              'info',
            ]);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'notifications',
        action: 'mark_all_read',
        metadata: { procId },
      });
    }
  };

  const markSingleNotificationAsSeen = async (notificationId: string) => {
    try {
      markSingleNotificationsAsSeen(
        {
          pathParams: {
            notificationId,
          },
        },
        {
          onSuccess: () => {
            // refetch global notifications
            queryClient.invalidateQueries([
              'globalNotificationsGetPaginated',
              activeOrganization?.id,
            ]);
            // refetch global notifications count
            queryClient.invalidateQueries([
              'notifications',
              activeOrganization?.id,
              'info',
            ]);
            // refetch notifications for this instance
            refetchNotifications();
            // refetch notifications count for this instance
            queryClient.invalidateQueries([
              'notifications',
              'instance',
              procId,
              'author',
              'info',
            ]);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'notifications',
        action: 'mark_notification_read',
        metadata: { notificationId },
      });
    }
  };

  const handleNotificationClick = (
    notification: NotificationRecordResponse,
  ) => {
    switch (notification.type) {
      case NotificationType.UserAddedComment:
        return trigger(EventTypes.NOTIFICATIONS_SIDEBAR_ITEM_CLICKED, {
          notification,
        });
      case NotificationType.UserCompletedProc:
        return markSingleNotificationAsSeen(notification.id);

      default:
        throw new Error(
          'Unknown notification type supplied to handleNotificationClick [NotificationsSidebar.tsx]',
        );
    }
  };

  return (
    <SidebarWrapper>
      <Row
        justifyContent="space-between"
        style={{
          borderBottom: `1px solid ${themeConfig[theme].colors.border}`,
        }}
      >
        <Row gap={0} style={{ width: 'fit-content' }}>
          <SidebarHeader
            style={{
              color: themeConfig[theme].colors.text,
              borderBottomColor: themeConfig[theme].colors.border,
            }}
          >
            Notifications
          </SidebarHeader>
          {Boolean(unreadNotificationsCount) && (
            <Tag
              color={themeConfig[theme].colors.primary}
              style={{
                marginLeft: -5,
                fontSize: 10,
                lineHeight: '16px',
                paddingInline: 5,
              }}
            >
              {unreadNotificationsCount || 0}
            </Tag>
          )}
        </Row>
        {Boolean(unreadNotificationsCount) && (
          <Button
            type="link"
            size="small"
            loading={notificationsLoading}
            onClick={markInstanceNotificationsAsSeen}
            style={{
              fontSize: 12,
              fontWeight: 500,
              color:
                theme === 'dark'
                  ? themeConfig[theme].colors.activeItemPrimaryColor
                  : themeConfig[theme].colors.primary,
            }}
          >
            Mark all as read
          </Button>
        )}
      </Row>
      <SidebarContent>
        <List
          itemLayout="horizontal"
          dataSource={(notificationsData as any) || []}
          style={{
            width: '100%',
          }}
          loadMore={
            hasNextPage && (
              <Row justifyContent="center" style={{ margin: '20px 0 30px' }}>
                <Button
                  size="small"
                  loading={notificationsLoading}
                  onClick={fetchNextPage}
                  style={{ fontSize: 12 }}
                >
                  Load more
                </Button>
              </Row>
            )
          }
          locale={{ emptyText: 'No notifications' }} // overrides default empty icon + text
          renderItem={(notification: NotificationRecordResponse) => (
            <NotificationItem
              $hoverBackground={themeConfig[theme].colors.contentBgSecondary}
              style={{
                color: themeConfig[theme].colors.text,
                borderColor: themeConfig[theme].colors.border,
                backgroundColor: notification.seen
                  ? themeConfig[theme].colors.contentBg
                  : themeConfig[theme].colors.activeItemSecondary,
              }}
              onClick={() => handleNotificationClick(notification)}
            >
              {notification.seen ? null : <NotificationDot />}
              {notificationContent(notification)}
              <Meta>{formatTimeAgo(notification.updatedAt)}</Meta>
            </NotificationItem>
          )}
        />
      </SidebarContent>
    </SidebarWrapper>
  );
};

const SidebarWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  overflow-y: hidden;
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
  padding: 15px;
  width: 100%;
`;

const SidebarContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
`;

const NotificationItem = styled.div<{
  $hoverBackground: string;
}>`
  padding: 12px 24px;
  font-size: 12px;
  border-bottom: 1px solid;
  line-height: 1.5;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${({ $hoverBackground }) => $hoverBackground} !important;
  }
  & strong,
`;

const Meta = styled.div`
  font-weight: 300;
  font-size: 10px;
  padding-top: 6px;
`;

const NotificationDot = styled.div`
  background: #ff4d4f;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  position: absolute;
  right: 10px;
  top: 10px;
`;
